import React, { useState, useMemo, useEffect } from "react";
import { CheckIcon } from "lucide-react";
import { usePermissions } from "../../../../PermissionsProvider";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import { cn } from "../../../../lib/utils";
import { AgentService } from "../../../../services/AgentService";
import { DatasetService } from "../../../../services/DatasetService";
import { WorkflowService, Workflow } from "../../../../services/WorkflowService";

import StepSetup from "./generate-config/StepSetup";
import StepPrompts from "./generate-config/StepPrompts";
import StepResponses from "./generate-config/StepResponses";
import StepPreview from "./generate-config/StepSeedPreview";
import PreviewModal from "./generate-config/PreviewModal";
import {
  GenerationConfig,
  AgentItem,
  SchemaField,
  estimateCost,
  getGenerationCostDimensions,
  buildMergedTeacherWorkflowInput,
  mergePlaceholdersWithGenerationVars,
} from "./generate-config/types";

export type { GenerationConfig, SchemaField, ComputedPlaceholder, GenerationVariable } from "./generate-config/types";
export type { GenerationMode, TeacherType } from "./generate-config/types";
export { GENERATION_DOMAINS, GENERATION_SIZE_TIERS } from "./generate-config/types";

export const CONFIG_SUB_STEPS = [
  { key: "setup", label: "Setup" },
  { key: "prompts", label: "Prompts" },
  { key: "responses", label: "Responses" },
  { key: "preview", label: "Preview" },
] as const;

export const CONFIG_SUB_STEP_COUNT = CONFIG_SUB_STEPS.length;

interface DatasetGenerateConfigProps {
  config: GenerationConfig;
  onChange: (config: Partial<GenerationConfig>) => void;
  intentGroupId?: number;
  configStep: number;
  setConfigStep: (step: number) => void;
}

const DatasetGenerateConfig: React.FC<DatasetGenerateConfigProps> = ({
  config,
  onChange,
  intentGroupId,
  configStep,
  setConfigStep,
}) => {
  const { apiKey } = usePermissions();
  const useProdSeeds = config.generation_use_prod_seeds;
  const isStructured = config.generation_mode === "structured";

  // ── Teacher data ──────────────────────────────────────────────────────
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [agents, setAgents] = useState<AgentItem[]>([]);

  useEffect(() => {
    const sub = WorkflowService.getWorkflows(null, apiKey).subscribe(
      (data: any) => setWorkflows(Array.isArray(data) ? data : []),
      () => setWorkflows([])
    );
    return () => sub.unsubscribe();
  }, [apiKey]);

  useEffect(() => {
    if (config.generation_teacher_type === "agent") {
      const sub = AgentService.getAgents(null, apiKey, false).subscribe(
        (data: any) => setAgents(data?.agents ?? (Array.isArray(data) ? data : [])),
        () => setAgents([])
      );
      return () => sub.unsubscribe();
    }
  }, [config.generation_teacher_type, apiKey]);

  // ── Auto-populate from workflow schemas ────────────────────────────────
  useEffect(() => {
    if (config.generation_teacher_type !== "workflow" || !config.generation_model || workflows.length === 0) return;
    const wf = workflows.find((w) => w.workflow_ref_name === config.generation_model);
    if (!wf?.meta) return;

    const updates: Partial<GenerationConfig> = {};

    const inputSchema = wf.meta.input_schema;
    const ph = mergePlaceholdersWithGenerationVars(
      config.generation_request_system_prompt || "",
      config.generation_user_template || "",
      config.generation_variables,
      config.generation_computed_placeholders
    );
    const hasSchema = inputSchema && typeof inputSchema === "object" && !Array.isArray(inputSchema);
    if (hasSchema) {
      updates.generation_workflow_input = buildMergedTeacherWorkflowInput(
        ph,
        inputSchema as Record<string, unknown>
      );
    } else if (ph.length > 0) {
      updates.generation_workflow_input = buildMergedTeacherWorkflowInput(ph, undefined);
    }

    const outputSchema = wf.meta.output_schema;
    if (outputSchema && typeof outputSchema === "object") {
      const fields: SchemaField[] = Object.entries(outputSchema).map(([key, field]) => ({
        name: key,
        type: (field.type || "string") as SchemaField["type"],
        description: field.description || "",
      }));
      updates.generation_response_schema = fields;
    }

    if (Object.keys(updates).length > 0) onChange(updates);
  }, [config.generation_model, config.generation_teacher_type, workflows]);

  // ── Cost estimate ─────────────────────────────────────────────────────
  const [serverCostEstimate, setServerCostEstimate] = useState<{
    input_tokens: number;
    output_tokens: number;
    cost_usd: number;
    is_estimated_pricing: boolean;
    line_items?: Array<{
      key: string;
      label: string;
      calls: number;
      input_tokens: number;
      output_tokens: number;
      usd: number;
      note?: string;
    }>;
    assumptions?: string[];
  } | null>(null);

  const clientCostEstimate = useMemo(
    () => estimateCost(config.generation_model, config.generation_size, config.generation_include_reasoning),
    [config.generation_model, config.generation_size, config.generation_include_reasoning]
  );

  const estimatePayload = useMemo(
    () => ({
      model: config.generation_model,
      size: config.generation_size,
      include_reasoning: config.generation_include_reasoning,
      mode: config.generation_mode,
      teacher_type: config.generation_teacher_type || "model",
      seed_model: config.generation_seed_model || "",
      use_prod_seeds: config.generation_use_prod_seeds,
      ...getGenerationCostDimensions(config),
    }),
    [
      config.generation_model,
      config.generation_size,
      config.generation_include_reasoning,
      config.generation_mode,
      config.generation_teacher_type,
      config.generation_seed_model,
      config.generation_use_prod_seeds,
      config.generation_variables,
      config.generation_computed_placeholders,
      config.generation_request_system_prompt,
      config.generation_user_template,
    ]
  );

  const debouncedEstimateKey = useDebouncedValue(JSON.stringify(estimatePayload), 500);

  useEffect(() => {
    let parsed: typeof estimatePayload;
    try {
      parsed = JSON.parse(debouncedEstimateKey) as typeof estimatePayload;
    } catch {
      setServerCostEstimate(null);
      return;
    }
    if (!parsed.model || parsed.size <= 0) {
      setServerCostEstimate(null);
      return;
    }
    const sub = DatasetService.estimateGenerationCost(
      {
        model: parsed.model,
        size: parsed.size,
        include_reasoning: parsed.include_reasoning,
        mode: parsed.mode,
        teacher_type: parsed.teacher_type,
        seed_model: parsed.seed_model,
        use_prod_seeds: parsed.use_prod_seeds,
        num_seed_variables: parsed.num_seed_variables,
        num_computed_variables: parsed.num_computed_variables,
        sequential_placeholders: parsed.sequential_placeholders,
      },
      null,
      apiKey
    ).subscribe(
      (r: any) =>
        setServerCostEstimate({
          input_tokens: r.input_tokens,
          output_tokens: r.output_tokens,
          cost_usd: r.cost_usd,
          is_estimated_pricing: r.is_estimated_pricing,
          line_items: r.line_items,
          assumptions: r.assumptions,
        }),
      () => setServerCostEstimate(null)
    );
    return () => sub.unsubscribe();
  }, [debouncedEstimateKey, apiKey]);

  const costEstimate = serverCostEstimate
    ? {
        inputTokens: serverCostEstimate.input_tokens,
        outputTokens: serverCostEstimate.output_tokens,
        costUsd: serverCostEstimate.cost_usd,
        isEstimatedPricing: serverCostEstimate.is_estimated_pricing,
        lineItems: serverCostEstimate.line_items ?? null,
        assumptions: serverCostEstimate.assumptions ?? null,
      }
    : clientCostEstimate
    ? {
        inputTokens: clientCostEstimate.inputTokens,
        outputTokens: clientCostEstimate.outputTokens,
        costUsd: clientCostEstimate.costUsd,
        isEstimatedPricing: clientCostEstimate.isEstimatedPricing,
        lineItems: null,
        assumptions: [
          "Could not load full estimate — showing rough teacher-only total from local pricing.",
        ],
      }
    : null;

  // ── Derived values ────────────────────────────────────────────────────
  const canPreview = !!config.generation_model && (
    useProdSeeds
      ? config.generation_prod_seed_ids.length >= 5 && (!isStructured || !!config.generation_user_template)
      : isStructured
      ? !!config.generation_user_template
      : config.generation_domains.length > 0
  );

  const teacherDisplayName = useMemo(() => {
    if (!config.generation_model) return "";
    if (config.generation_teacher_type === "workflow") {
      const wf = workflows.find((w) => w.workflow_ref_name === config.generation_model);
      return wf ? wf.workflow_name : config.generation_model;
    }
    if (config.generation_teacher_type === "agent") {
      const ag = agents.find((a) => String(a.id) === config.generation_model);
      return ag ? ag.agent_name : config.generation_model;
    }
    const parts = config.generation_model.split(":");
    return parts.length > 1 ? parts.slice(1).join(":") : config.generation_model;
  }, [config.generation_model, config.generation_teacher_type, workflows, agents]);

  // ── Preview ───────────────────────────────────────────────────────────
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewExamples, setPreviewExamples] = useState<
    { domain: string; difficulty: string; messages: any[]; model: string; usage?: any }[]
  >([]);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  const handleGeneratePreview = () => {
    if (!canPreview) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewExamples([]);
    setPreviewModalVisible(true);
    setPreviewIndex(0);

    const blankInputSide = useProdSeeds && !isStructured;

    DatasetService.streamPreviewGeneration(
      {
        mode: config.generation_mode,
        teacher_type: config.generation_teacher_type || "model",
        teacher_ref: config.generation_model,
        model: config.generation_model,
        seed_model: config.generation_seed_model || "",
        domains: blankInputSide ? [] : config.generation_domains,
        context: blankInputSide ? "" : (config.generation_context || ""),
        include_reasoning: config.generation_include_reasoning,
        response_instructions: config.generation_response_instructions || "",
        request_system_prompt: config.generation_request_system_prompt || "",
        teacher_system_prompt:
          config.generation_teacher_type === "model"
            ? (config.generation_teacher_system_prompt || "")
            : "",
        user_message_template: blankInputSide ? "" : (config.generation_user_template || ""),
        user_example: blankInputSide ? "" : (config.generation_user_example || ""),
        response_example: config.generation_response_example || "",
        difficulty_cues: config.generation_difficulty_cues || "",
        seed_instructions: config.generation_seed_instructions || "",
        workflow_input: blankInputSide ? "" : (config.generation_workflow_input || ""),
        teacher_response_value_path: (config.generation_teacher_response_value_path || "").trim() || undefined,
        response_schema: config.generation_response_schema || [],
        computed_placeholders: config.generation_computed_placeholders || [],
        generation_variables:
          config.generation_variables && config.generation_variables.length > 0
            ? config.generation_variables
            : undefined,
        max_tokens:
          config.generation_teacher_type === "model" ? config.generation_max_tokens : null,
        count: 6,
        prod_seed_ids: useProdSeeds ? config.generation_prod_seed_ids : undefined,
      },
      (example: { domain: string; difficulty: string; messages: any[]; model: string; usage?: any }) => setPreviewExamples((prev) => [...prev, example]),
      () => setPreviewLoading(false),
      (errorMsg: string) => { setPreviewError(errorMsg || "Failed to generate preview."); setPreviewLoading(false); },
      apiKey
    ).catch((err: unknown) => {
      console.error("Preview stream error:", err);
      setPreviewError("Failed to generate preview. Please try again.");
      setPreviewLoading(false);
    });
  };

  // ── Step completion heuristics ────────────────────────────────────────
  const seedsStepComplete = !useProdSeeds || config.generation_prod_seed_ids.length >= 5;
  const setupStepComplete = config.generation_size > 0 && seedsStepComplete;
  const promptStepComplete =
    isStructured
      ? !!config.generation_user_template
      : useProdSeeds
        ? true
        : config.generation_domains.length > 0;

  const stepComplete = [
    setupStepComplete,
    promptStepComplete,
    !!config.generation_model,
    false,
  ];

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Progress indicator — label under each step for wayfinding */}
      <div className="flex flex-row flex-wrap items-start justify-center gap-y-2 px-1">
        {CONFIG_SUB_STEPS.map((step, idx) => (
          <React.Fragment key={step.key}>
            {idx > 0 && (
              <div
                className={cn(
                  "h-px w-6 sm:w-10 shrink-0 mt-3.5 transition-colors",
                  idx <= configStep ? "bg-maitai-green" : "bg-border"
                )}
                aria-hidden
              />
            )}
            <button
              type="button"
              onClick={() => setConfigStep(idx)}
              className="group flex flex-col items-center gap-1.5 w-[4.5rem] sm:w-24 shrink-0"
            >
              <div
                className={cn(
                  "flex items-center justify-center w-7 h-7 rounded-full text-xs font-gc-med transition-colors",
                  idx === configStep
                    ? "bg-maitai-green text-white ring-2 ring-maitai-green/25"
                    : stepComplete[idx]
                      ? "bg-maitai-green text-white"
                      : "bg-muted text-muted-foreground group-hover:bg-muted-foreground/20"
                )}
              >
                {stepComplete[idx] && idx !== configStep ? (
                  <CheckIcon className="w-3.5 h-3.5" />
                ) : (
                  <span>{idx + 1}</span>
                )}
              </div>
              <span
                className={cn(
                  "text-center text-[10px] sm:text-xs font-gc-reg leading-snug transition-colors px-0.5",
                  idx === configStep
                    ? "text-foreground font-gc-med"
                    : stepComplete[idx]
                      ? "text-maitai-green"
                      : "text-muted-foreground group-hover:text-foreground"
                )}
              >
                {step.label}
              </span>
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Sub-tab content */}
      {configStep === 0 && (
        <StepSetup config={config} onChange={onChange} intentGroupId={intentGroupId} />
      )}

      {configStep === 1 && (
        <StepPrompts
          config={config}
          onChange={onChange}
          workflows={workflows}
        />
      )}

      {configStep === 2 && (
        <StepResponses
          config={config}
          onChange={onChange}
          workflows={workflows}
          agents={agents}
        />
      )}

      {configStep === 3 && (
        <StepPreview
          config={config}
          costEstimate={costEstimate}
          teacherDisplayName={teacherDisplayName}
          canPreview={canPreview}
          previewLoading={previewLoading}
          onGeneratePreview={handleGeneratePreview}
        />
      )}

      {/* Preview Modal */}
      <PreviewModal
        open={previewModalVisible}
        onOpenChange={setPreviewModalVisible}
        loading={previewLoading}
        error={previewError}
        examples={previewExamples}
        index={previewIndex}
        setIndex={setPreviewIndex}
        onRegenerate={handleGeneratePreview}
        teacherDisplayName={teacherDisplayName}
      />
    </div>
  );
};

export default DatasetGenerateConfig;
